import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsive } from '../../hooks/useResponsive';
import api from '../../services/api';
import ContainerDrilldownModal from '../shared/ContainerDrilldownModal';

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (page: string) => void;
}

interface SearchItem {
  id: string;
  label: string;
  category: 'pages' | 'actions' | 'recent' | 'containers';
  description?: string;
  icon?: string;
  shortcut?: string;
  containerNumber?: string;
}

interface SearchCategory {
  name: string;
  items: SearchItem[];
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose, onNavigate }) => {
  const { theme, bgMode } = useTheme();
  const { isMobile } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Container search results (fetched from backend)
  const [containerResults, setContainerResults] = useState<SearchItem[]>([]);
  const [containerSearching, setContainerSearching] = useState(false);
  // When a container is selected we open this drilldown modal
  const [drilldownContainer, setDrilldownContainer] = useState<string | null>(null);

  // Define all available items
  const allItems: SearchItem[] = [
    // Pages
    { id: 'dashboard', label: 'Dashboard', category: 'pages', description: 'View main dashboard', icon: '📊' },
    { id: 'inventory', label: 'Inventory', category: 'pages', description: 'Manage inventory', icon: '📦' },
    { id: 'inbound', label: 'Inbound', category: 'pages', description: 'Inbound operations', icon: '📥' },
    { id: 'outbound', label: 'Outbound', category: 'pages', description: 'Outbound operations', icon: '📤' },
    { id: 'transport', label: 'Transport', category: 'pages', description: 'Transport management', icon: '🚚' },
    { id: 'bookings', label: 'Bookings', category: 'pages', description: 'Manage bookings', icon: '📋' },
    { id: 'documents', label: 'Documents', category: 'pages', description: 'View documents', icon: '📄' },
    { id: 'appointments', label: 'Appointments', category: 'pages', description: 'Manage appointments', icon: '📅' },
    { id: 'expiry-tracker', label: 'Expiry Tracker', category: 'pages', description: 'Track expiring items', icon: '⏰' },
    // 'alarms' entry removed (May 17, 2026) — page deprecated, search points
    // straight at the replacement.
    { id: 'alert-rules', label: 'Alert Rules', category: 'pages', description: 'Create + manage alert rules', icon: '🔔' },
    { id: 'reports', label: 'Reports', category: 'pages', description: 'Generate reports', icon: '📈' },
    { id: 'settings', label: 'Settings', category: 'pages', description: 'System settings', icon: '⚙️' },
    { id: 'halley-ai', label: 'Halley AI', category: 'pages', description: 'AI assistant', icon: '🤖' },
    { id: 'announcements', label: 'Announcements', category: 'pages', description: 'View announcements', icon: '📢' },
    { id: 'chat', label: 'Chat', category: 'pages', description: 'Team chat', icon: '💬' },

    // Quick Actions
    { id: 'expired-items', label: 'Show expired items', category: 'actions', description: 'Display all expired inventory items', icon: '⏱️', shortcut: 'Cmd+E' },
    { id: 'view-capacity', label: 'View capacity', category: 'actions', description: 'Check warehouse capacity', icon: '📏', shortcut: 'Cmd+C' },
    { id: 'inbound-today', label: 'Check inbound today', category: 'actions', description: 'View today\'s inbound', icon: '📥', shortcut: 'Cmd+I' },
    { id: 'generate-report', label: 'Generate report', category: 'actions', description: 'Create a new report', icon: '📝', shortcut: 'Cmd+R' },
    { id: 'stock-reconciliation', label: 'View stock reconciliation', category: 'actions', description: 'Run stock reconciliation', icon: '✓', shortcut: 'Cmd+S' },
  ];

  // Debounced container search (hits backend when query >= 2 chars)
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setContainerResults([]);
      setContainerSearching(false);
      return;
    }
    setContainerSearching(true);
    const handle = window.setTimeout(async () => {
      try {
        const res: any = await api.searchContainers(q, 8);
        const rows: any[] = (res && (res.results || res.items || res)) || [];
        const items: SearchItem[] = rows.map((r: any, i: number): SearchItem => {
          const num = r.container_number || r.containerNumber || r.container || '';
          const ref = r.booking_reference || r.booking_ref || '';
          const cust = r.customer_name || r.customer || '';
          const wh = r.warehouse_code || r.warehouse || '';
          return {
            id: `container:${num}:${i}`,
            label: num || ref || `Container #${i + 1}`,
            category: 'containers' as const,
            description: [ref, cust, wh].filter(Boolean).join(' • '),
            icon: '📦',
            containerNumber: num,
          };
        }).filter((it: SearchItem) => !!it.containerNumber);
        setContainerResults(items);
      } catch {
        setContainerResults([]);
      } finally {
        setContainerSearching(false);
      }
    }, 220);
    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return allItems;
    }

    const query = searchQuery.toLowerCase();
    const localMatches = allItems.filter((item) => {
      const matchLabel = item.label.toLowerCase().includes(query);
      const matchDescription = item.description?.toLowerCase().includes(query);
      return matchLabel || matchDescription;
    });
    // Containers come first so the user sees them prominently
    return [...containerResults, ...localMatches];
  }, [searchQuery, containerResults]);

  // Group filtered items by category
  const groupedItems = useMemo((): SearchCategory[] => {
    const grouped: Record<string, SearchItem[]> = {
      containers: [],
      pages: [],
      actions: [],
      recent: [],
    };

    filteredItems.forEach((item) => {
      if (grouped[item.category]) {
        grouped[item.category].push(item);
      }
    });

    const categories: SearchCategory[] = [];
    if (grouped.containers.length > 0) {
      categories.push({ name: 'CONTAINERS', items: grouped.containers });
    }
    if (grouped.pages.length > 0) {
      categories.push({ name: 'PAGES', items: grouped.pages });
    }
    if (grouped.actions.length > 0) {
      categories.push({ name: 'QUICK ACTIONS', items: grouped.actions });
    }
    if (grouped.recent.length > 0) {
      categories.push({ name: 'RECENT SEARCHES', items: grouped.recent });
    }

    return categories;
  }, [filteredItems]);

  // Flatten items for keyboard navigation
  const flattenedItems = useMemo(() => {
    return groupedItems.reduce((acc: SearchItem[], category) => [...acc, ...category.items], []);
  }, [groupedItems]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        // This would be handled by parent component, but included for completeness
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle keyboard navigation within modal
  const handleModalKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(flattenedItems.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev === 0 ? Math.max(flattenedItems.length - 1, 0) : prev - 1));
    } else if (e.key === 'Enter' && flattenedItems.length > 0) {
      e.preventDefault();
      const selectedItem = flattenedItems[selectedIndex];
      if (selectedItem) {
        activateItem(selectedItem);
      }
    }
  };

  const activateItem = (item: SearchItem) => {
    if (item.category === 'containers' && item.containerNumber) {
      setDrilldownContainer(item.containerNumber);
      return; // keep the GlobalSearch open behind the drilldown
    }
    onNavigate(item.id);
    onClose();
  };

  // Handle outside click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  const selectedItem = flattenedItems[selectedIndex];

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: isMobile ? 40 : 80,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.15s ease-out',
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      <div
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: isMobile ? 'calc(100% - 32px)' : '90%',
          maxWidth: 560,
          maxHeight: isMobile ? 'calc(100vh - 120px)' : '70vh',
          background: bgMode.cardBg,
          borderRadius: 16,
          boxShadow: `0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08)`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'slideDown 0.2s ease-out',
        }}
      >
        {/* Search Input */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${bgMode.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            stroke={bgMode.textMuted}
            strokeWidth={2}
            style={{ flexShrink: 0 }}
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>

          <input
            ref={inputRef}
            type="text"
            placeholder="Search containers, pages, actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleModalKeyDown}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              color: bgMode.text,
              fontSize: 16,
              fontWeight: 500,
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Results List */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {containerSearching && (
            <div style={{ padding: '8px 24px', fontSize: 11, color: bgMode.textMuted, letterSpacing: 0.4 }}>
              Searching containers…
            </div>
          )}
          {flattenedItems.length === 0 ? (
            <div
              style={{
                padding: '48px 24px',
                textAlign: 'center',
                color: bgMode.textMuted,
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 14 }}>No results for &quot;{searchQuery}&quot;</div>
            </div>
          ) : (
            groupedItems.map((category, categoryIndex) => (
              <div key={category.name} style={{ padding: '12px 0' }}>
                {/* Category Header */}
                <div
                  style={{
                    padding: '8px 24px',
                    color: bgMode.textMuted,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                  }}
                >
                  {category.name}
                </div>

                {/* Category Items */}
                {category.items.map((item, itemIndex) => {
                  // Calculate global index for highlighting
                  const globalIndex = groupedItems
                    .slice(0, categoryIndex)
                    .reduce((sum, cat) => sum + cat.items.length, 0) + itemIndex;

                  const isSelected = globalIndex === selectedIndex;

                  return (
                    <div
                      key={item.id}
                      onClick={() => activateItem(item)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      style={{
                        padding: '10px 24px',
                        cursor: 'pointer',
                        background: isSelected ? bgMode.bgHover : 'transparent',
                        transition: 'background-color 0.1s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      {/* Icon */}
                      <div style={{ fontSize: 18, flexShrink: 0 }}>
                        {item.icon || '•'}
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            color: bgMode.text,
                            fontSize: 14,
                            fontWeight: 500,
                            marginBottom: item.description ? 4 : 0,
                          }}
                        >
                          {item.label}
                        </div>
                        {item.description && (
                          <div
                            style={{
                              color: bgMode.textMuted,
                              fontSize: 12,
                              lineHeight: '1.4',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.description}
                          </div>
                        )}
                      </div>

                      {/* Shortcut Badge */}
                      {item.shortcut && (
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: bgMode.textMuted,
                            background: bgMode.bgSurface,
                            padding: '4px 8px',
                            borderRadius: 6,
                            border: `1px solid ${bgMode.border}`,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {item.shortcut}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 24px',
            borderTop: `1px solid ${bgMode.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 12,
            color: bgMode.textMuted,
            background: bgMode.bgRaised,
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <kbd
                style={{
                  padding: '2px 6px',
                  background: bgMode.inputBg,
                  border: `1px solid ${bgMode.border}`,
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                ↑↓
              </kbd>
              <span>Navigate</span>
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <kbd
                style={{
                  padding: '2px 6px',
                  background: bgMode.inputBg,
                  border: `1px solid ${bgMode.border}`,
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                ↵
              </kbd>
              <span>Select</span>
            </div>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <kbd
                style={{
                  padding: '2px 6px',
                  background: bgMode.inputBg,
                  border: `1px solid ${bgMode.border}`,
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                ESC
              </kbd>
              <span>Close</span>
            </div>
          </div>
          <div style={{ color: bgMode.textMuted, fontSize: 11 }}>
            {flattenedItems.length} result{flattenedItems.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Container drilldown opens on top when a container result is chosen */}
      <ContainerDrilldownModal
        open={!!drilldownContainer}
        containerNumber={drilldownContainer}
        onClose={() => setDrilldownContainer(null)}
      />
    </div>
  );
};

export default GlobalSearch;
